import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RemoveModeratorIcon from "@mui/icons-material/RemoveModerator";

/** A ação é destrutiva: o ícone do título troca o azul do tema pelo vermelho. */
const destructiveTitleSx = {
  "& svg": {
    color: "error.main",
    backgroundColor: (theme: any) => alpha(theme.palette.error.light, 0.35),
  },
};

export type DemotionPhase = "loading" | "form" | "submitting" | "success";

export interface DemoteResellerModalDelegate {
  onConfirmDemotion: () => void;
  onCloseDemotion: () => void;
  onRetryDemotionImpact: () => void;
}

interface Props {
  isOpen: boolean;
  phase: DemotionPhase;
  errorMessage?: string;
  userName: string;
  impactData: { clientsCount: number; activeLicensesCount: number } | null;
  delegate: DemoteResellerModalDelegate;
}

export function DemoteResellerModal({
  isOpen,
  phase,
  errorMessage,
  userName,
  impactData,
  delegate,
}: Props) {
  const renderLoading = (label?: string) => (
    <Box
      alignItems="center"
      display="flex"
      flexDirection="column"
      justifyContent="center"
      gap={2}
      paddingY={5}
    >
      <CircularProgress color={label ? "error" : "primary"} />
      {label && (
        <Typography variant="body2" color="text.secondary">
          {label}
        </Typography>
      )}
    </Box>
  );

  const renderSuccess = () => (
    <Box
      alignItems="center"
      display="flex"
      flexDirection="column"
      justifyContent="center"
      gap={1}
      paddingY={5}
    >
      <CheckCircleIcon sx={{ fontSize: 56, color: "success.main" }} />
      <Typography variant="subtitle1" color="success.main" fontWeight="bold">
        Cargo removido!
      </Typography>
      <Typography variant="body2" color="text.secondary" align="center">
        <strong>{userName}</strong> não é mais um Revendedor.
      </Typography>
    </Box>
  );

  const renderImpact = () => (
    <>
      <Typography color="text.secondary">
        Tem certeza de que deseja remover o cargo de <strong>Revendedor</strong>{" "}
        do usuário <strong>{userName}</strong>?
      </Typography>

      <Alert severity="info">
        <strong>Importante:</strong> ao confirmar, será apenas removido o cargo
        de Revendedor.
        <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
          <li>Dados cadastrais permanecerão intactos;</li>
          <li>Histórico de clientes não será excluído;</li>
          <li>Registros de licenças não serão alterados;</li>
        </Box>
        <Box sx={{ mt: 1 }}>
          <strong>Para sua informação,</strong> enquanto exerceu esse papel, ele
          acumulou:
          <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
            <li>
              <strong>{impactData!.clientsCount}</strong> clientes vinculados
            </li>
            <li>
              <strong>{impactData!.activeLicensesCount}</strong> licenças ativas
              distribuídas
            </li>
          </Box>
        </Box>
      </Alert>
    </>
  );

  const renderForm = () => (
    <Box display="flex" flexDirection="column" sx={{ gap: 2 }}>
      {errorMessage && (
        <Alert
          severity="error"
          action={
            !impactData ? (
              <Button
                color="inherit"
                size="small"
                onClick={delegate.onRetryDemotionImpact}
              >
                Tentar novamente
              </Button>
            ) : undefined
          }
        >
          {errorMessage}
        </Alert>
      )}

      {impactData && renderImpact()}
    </Box>
  );

  return (
    <Dialog
      open={isOpen}
      onClose={() => {
        if (phase === "submitting") {
          return;
        }
        delegate.onCloseDemotion();
      }}
      variant="modal"
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle variant="modal" sx={destructiveTitleSx}>
        <RemoveModeratorIcon />
        Remover Cargo de Revendedor
      </DialogTitle>
      <DialogContent variant="modal">
        {phase === "loading" && renderLoading()}
        {phase === "submitting" && renderLoading("Removendo cargo...")}
        {phase === "success" && renderSuccess()}
        {phase === "form" && renderForm()}
      </DialogContent>
      {phase === "form" && (
        <DialogActions
          variant="modal"
          sx={{ flexWrap: "wrap", gap: 1, justifyContent: "end" }}
        >
          <Button
            variant="outlined"
            color="error"
            onClick={delegate.onCloseDemotion}
          >
            {impactData ? "Cancelar" : "Fechar"}
          </Button>

          {impactData && (
            <Button
              variant="contained"
              color="error"
              onClick={delegate.onConfirmDemotion}
            >
              Confirmar Remoção
            </Button>
          )}
        </DialogActions>
      )}
    </Dialog>
  );
}
